import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MapPin, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { CONTACT } from "../lib/content";
import { FadeUp, PageHero } from "../components/Reveal";

const SUBJECTS = ["General enquiry", "Jewellery purchase", "Bespoke jewellery", "Repair", "Valuation", "Cleaning", "Engraving", "Other"];
const field = "w-full bg-ink-2 border border-white/10 focus:border-gold outline-none px-4 py-3.5 text-sm text-ivory placeholder:text-stone-mist/50 transition-colors";

export default function Contact() {
  const [params] = useSearchParams();
  const initial = params.get("subject") || "";
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: SUBJECTS.includes(initial) ? initial : initial ? "Other" : "General enquiry", message: initial && !SUBJECTS.includes(initial) ? initial : "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", { type: "contact", ...form });
      setSent(true);
      toast.success("Message sent — we'll be in touch.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <PageHero eyebrow="Contact" title="Let's Talk Jewellery" sub="Visit the shop, call us, or send a message — we're always happy to help." />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-12">
        <FadeUp>
          <div className="border border-white/10 bg-ink-2/50 p-8 space-y-7">
            <div>
              <h2 className="font-serif text-2xl text-ivory font-light mb-1">Celtic Gold Ltd</h2>
              <p className="text-stone-mist text-sm leading-relaxed">{CONTACT.address.join(", ")}</p>
            </div>
            <a href={`tel:${CONTACT.phoneShort.replace(/\s/g, "")}`} className="flex items-center gap-3 text-ivory hover:text-gold transition-colors" data-testid="contact-phone">
              <Phone className="w-4 h-4 text-gold" /> {CONTACT.phone}
            </a>
            <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 text-ivory hover:text-gold transition-colors" data-testid="contact-email">
              <Mail className="w-4 h-4 text-gold" /> {CONTACT.email}
            </a>
            <div className="flex items-start gap-3 text-stone-mist text-sm">
              <MapPin className="w-4 h-4 text-gold mt-0.5 shrink-0" />
              <span>Find us on Michael Street, a short walk from Peel Castle and the harbour.</span>
            </div>
            <div className="border border-gold/25 bg-gold/5 p-5">
              <p className="text-sm text-ivory font-light">Prefer to speak to us?</p>
              <p className="text-gold font-mono text-sm mt-1">Call {CONTACT.phoneShort}</p>
            </div>
            <div className="border border-white/10 overflow-hidden">
              <iframe
                title="Celtic Gold location map"
                src="https://www.openstreetmap.org/export/embed.html?bbox=-4.7200%2C54.2200%2C-4.6900%2C54.2320&layer=mapnik&marker=54.2257%2C-4.6985"
                className="w-full h-48 grayscale invert-[0.9] contrast-[0.9]"
                loading="lazy"
                data-testid="contact-map"
              />
            </div>
          </div>
        </FadeUp>

        <FadeUp delay={0.1}>
          {sent ? (
            <div className="border border-gold/30 bg-gold/5 p-10 text-center" data-testid="contact-success">
              <h2 className="font-serif text-3xl text-ivory font-light mb-4">Thank you</h2>
              <p className="text-stone-mist font-light">Your message has been sent. We aim to respond as soon as we can — for anything urgent, call {CONTACT.phoneShort}.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-white/10 bg-ink-2/50 p-7 space-y-4" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input required value={form.name} onChange={set("name")} placeholder="Name *" className={field} data-testid="contact-name" />
                <input required type="email" value={form.email} onChange={set("email")} placeholder="Email *" className={field} data-testid="contact-email-input" />
                <input value={form.phone} onChange={set("phone")} placeholder="Telephone" className={field} data-testid="contact-phone-input" />
                <select value={form.subject} onChange={set("subject")} className={field} data-testid="contact-subject">
                  {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <textarea required value={form.message} onChange={set("message")} placeholder="Your message *" rows={7} className={field} data-testid="contact-message" />
              <button type="submit" disabled={sending} data-testid="contact-submit" className="w-full sm:w-auto bg-gold text-ink px-10 py-4 text-xs uppercase tracking-[0.25em] font-medium hover:bg-gold-glow transition-colors duration-300 disabled:opacity-50">
                {sending ? "Sending..." : "Send Message"}
              </button>
            </form>
          )}
        </FadeUp>
      </section>
    </main>
  );
}
